import { Logout } from '@mui/icons-material';
import { Box, Tooltip } from '@mui/material';
import { useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import logoRegie from '../../assets/img/LogoRegie.png';
import { getValueLocalStorage } from '../../hooks/use-local-storage.hook';
import { ConnectedUserDto } from '../../services/dtos/connected-user-dto';
import { UserAccountDto } from '../../services/dtos/user-account.dto';
import { isAdminUser } from '../../utils/access-role.util';
import './header.scss';

function getPageAdminLabel(pageKey: string): string {
    switch (pageKey) {
        case 'politique-confidentialite':
            return 'Politique de Confidentialité';
        case 'mentions-legales':
        default:
            return 'Mentions légales';
    }
}

/**
 * Header de l'application
 * @param props
 * @constructor
 */
export default function Header(
    props: Readonly<{
        user: null | ConnectedUserDto;
        signoutLoggedInAs: () => void;
        signout: () => void;
    }>
) {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const isAdmin = isAdminUser(props.user);
    const showAdminActions = isAdmin && !props.user?.emailLoggedInAS;
    const displayName = [props.user?.firstName, props.user?.lastName].filter(Boolean).join(' ').trim();
    let adminLabel = '';
    if (props.user?.emailLoggedInAS) {
        adminLabel = ` (${props.user.emailLoggedInAS})`;
    } else if (isAdmin) {
        adminLabel = ' (Admin)';
    }
    const userLabel = `${displayName || 'Utilisateur connecté'}${adminLabel}`;
    const subtitle = useMemo(() => {
        const mandateId = searchParams.get('m');
        const establishmentId = searchParams.get('e');
        const userAccount = (getValueLocalStorage('userAccount') as UserAccountDto | null) ?? ({} as UserAccountDto);

        return getSubtitle(location.pathname, mandateId, establishmentId, userAccount);
    }, [location.pathname, searchParams]);

    return (
        <Box component="header" className="container-header">
            <Box className="header-toolbar">
                <Box className="header-shell">
                    <Box className="header-brand">
                        <button
                            type="button"
                            className="header-logo-button"
                            data-testid="logo-home"
                            aria-label="Accueil"
                            onClick={() => {
                                navigate('/');
                            }}
                        >
                            <img className="header-logo" src={logoRegie} alt="Logo SDDEA" />
                        </button>
                        {subtitle && <h4>{subtitle}</h4>}
                    </Box>

                    <Box component="nav" className="header-nav" aria-label="Navigation du compte">
                        <button
                            type="button"
                            data-testid="btn-home"
                            className="header-link"
                            onClick={() => {
                                navigate('/');
                            }}
                        >
                            Accueil
                        </button>
                        {props.user && (
                            <button
                                type="button"
                                data-testid="btn-account"
                                className="header-link"
                                onClick={() => {
                                    navigate('/compte');
                                }}
                            >
                                Mon compte
                            </button>
                        )}
                        {props.user && (
                            <button
                                type="button"
                                data-testid="btn-contacts"
                                className="header-link"
                                onClick={() => {
                                    navigate('/contacts');
                                }}
                            >
                                Mes contacts
                            </button>
                        )}
                        {props.user && showAdminActions && (
                            <button
                                type="button"
                                className="header-link"
                                data-testid="update-legales-notices"
                                onClick={() => {
                                    navigate('/modifier-mentions-legales');
                                }}
                            >
                                Modifier &quot;{getPageAdminLabel('mentions-legales')}&quot;
                            </button>
                        )}
                        {props.user && showAdminActions && (
                            <button
                                type="button"
                                className="header-link"
                                data-testid="update-privacy-policy"
                                onClick={() => {
                                    navigate('/modifier-politique-confidentialite');
                                }}
                            >
                                Modifier &quot;{getPageAdminLabel('politique-confidentialite')}&quot;
                            </button>
                        )}
                        {props.user && showAdminActions && (
                            <button
                                type="button"
                                className="header-link"
                                onClick={() => {
                                    navigate('/administration/se-connecter-en-tant-que');
                                }}
                            >
                                Se connecter en tant que
                            </button>
                        )}
                        {props.user && showAdminActions && (
                            <button
                                type="button"
                                className="header-link"
                                data-testid="btn-connections-stats"
                                onClick={() => {
                                    navigate('/administration/connexions');
                                }}
                            >
                                Connexions
                            </button>
                        )}
                    </Box>

                    {props.user ? (
                        <Box className="header-user">
                            <p className="header-user-name">{userLabel}</p>
                            <Box className="header-user-actions">
                                {props.user?.emailLoggedInAS && (
                                    <Tooltip title="Se déconnecter en tant que" arrow>
                                        <button
                                            type="button"
                                            aria-label="Se déconnecter en tant que"
                                            className="header-icon-button header-user-logout-as"
                                            onClick={props.signoutLoggedInAs}
                                        >
                                            <Logout fontSize="small" />
                                        </button>
                                    </Tooltip>
                                )}
                                <Tooltip title="Déconnexion" arrow>
                                    <button
                                        type="button"
                                        data-testid="btn-disconnect"
                                        aria-label="Déconnexion"
                                        className="header-icon-button header-user-logout"
                                        onClick={props.signout}
                                    >
                                        <Logout fontSize="small" />
                                    </button>
                                </Tooltip>
                            </Box>
                        </Box>
                    ) : (
                        <Box className="header-user-placeholder" aria-hidden="true" />
                    )}
                </Box>
            </Box>
        </Box>
    );
}

/**
 * Fonction qui permet de gérer les sous titres utiles dans le header et les pdf
 * @param path url courrante
 * @param mandateId identifiant du mandat
 * @param establishmentId identifiant de l'établissement
 * @param userAccount personne connectée
 */
export function getSubtitle(
    path: string,
    mandateId: string | null,
    establishmentId: string | null,
    userAccount: UserAccountDto
) {
    switch (path) {
        case '/compte':
            return 'Mon compte';
        case '/contacts':
            return 'Mes contacts';
        case '/elu/ag':
            return 'Assemblée générale';
        case '/elu/bsca':
            return "Bureau Syndical et Conseil d'Administration";
        default:
            return (
                getMandatesEstablishmentSubtitle(mandateId, establishmentId, userAccount, path) ||
                getMandatesBasinSubtitle(mandateId, userAccount, path) ||
                getMandatesTerritorySubtitle(mandateId, userAccount, path)
            );
    }
}

/**
 * Checks if the given mandateId exists
 * @param {string|null} mandateId - mandate Id
 * @returns {boolean} - true if mandate id exists else false
 */
const hasMandateId = (mandateId: string | null): boolean => !!mandateId;

/**
 * Checks if the given path includes the given sub-path
 * @param {string} path - full path
 * @param {string} subPath - sub path
 * @returns {boolean} - true if path includes sub-path else false
 */
const isPathIncludes = (path: string, subPath: string): boolean => path.includes(subPath);

/**
 * Méthode qui retourne le sous titre d'un mandate de type etablissement
 * @param path url courrante
 * @param mandateId identifiant du mandat
 * @param establishmentId identifiant de l'établissement
 * @param userAccount personne connectée
 */
const getMandatesEstablishmentSubtitle = (
    mandateId: string | null,
    establishmentId: string | null,
    userAccount: UserAccountDto,
    path: string
): string | null => {
    if (hasMandateId(mandateId) && isPathIncludes(path, 'elu/eau')) {
        const establishment = userAccount.mandatesEstablishment?.find(
            value =>
                value.id.toString() === mandateId?.toString() &&
                value.establishmentId.toString() === establishmentId?.toString()
        );
        if (establishment) {
            return `COPE : ${establishment.establishmentLabel}`;
        }
    }
    return null;
};

/**
 * Méthode qui retourne le sous titre d'un mandate de type bassin
 * @param path url courrante
 * @param mandateId identifiant du mandat
 * @param userAccount personne connectée
 */
const getMandatesBasinSubtitle = (
    mandateId: string | null,
    userAccount: UserAccountDto,
    path: string
): string | null => {
    if (hasMandateId(mandateId) && isPathIncludes(path, 'elu/bassin')) {
        const mandate = userAccount.mandatesBasin?.find(value => value.id.toString() === mandateId);
        if (mandate) {
            return `Bassin : ${mandate.title}`;
        }
    }
    return null;
};

/**
 * Méthode qui retourne le sous titre d'un mandate de type territoire
 * @param path url courrante
 * @param mandateId identifiant du mandat
 * @param userAccount personne connectée
 */
const getMandatesTerritorySubtitle = (
    mandateId: string | null,
    userAccount: UserAccountDto,
    path: string
): string | null => {
    if (hasMandateId(mandateId) && isPathIncludes(path, 'elu/territoire')) {
        const mandate = userAccount.mandatesTerritory?.find(value => value.id.toString() === mandateId);
        if (mandate) {
            return `Territoire : ${mandate.territoryLabel}`;
        }
    }
    return null;
};
